import React from 'react';
import ActionErrorMessage from '~/components/actions/ActionErrorMessage';
import { observer } from 'mobx-react';
import { ActionSteps, ActionStepProgress } from '~/components/actions/Actions';
import { Action, Console } from '~/lib/ApiModel';
import './ActionMonitor.scss';
import { get } from '~/lib/helper/ObjectHelper';
import { hasValue } from '~/lib/helper/ArrayHelper';
import { Alert } from 'reactstrap';
import { getAppContext } from '~/lib/AppContext';
import { autorun, computed, makeObservable, observable, runInAction } from 'mobx';
import { getServerIdFromActionResource, shouldShowConsole } from '~/lib/helper/ActionHelper';
import { AsyncStore } from '~/lib/store/AsyncStore';
import UnmountHelper from '~/lib/helper/UnmountHelper';
import ConsolePane from '~/pages/server/components/ConsolePane';
import { BillingInvoicesRoute } from '~/lib/helper/RouteHelper';

type ActionMonitorProps = {
    action: Action;
    title: string;
};

@observer
export default class ActionMonitor extends React.Component<ActionMonitorProps> {
    // Mirror of props.action: props isn't observable, so the computed below reads this
    // local observable instead (synced in componentDidUpdate). `.ref` because the store
    // may hand back a replacement instance whose own fields are already observable.
    @observable.ref
    private action = this.props.action;

    consoleStore = new AsyncStore(async () => {
        // Read the mirror, not props: the loader body runs synchronously (up to its
        // first await) inside the componentDidMount autorun's tracked derivation.
        const serverId = getServerIdFromActionResource(this.action);
        if (hasValue(serverId)) return getAppContext().apiClient.getConsoleForServer(serverId);
    }, undefined);

    @computed
    get showConsole() {
        return shouldShowConsole(this.action);
    }

    constructor(props: ActionMonitorProps) {
        super(props);
        makeObservable(this);
    }

    componentDidUpdate(prevProps: ActionMonitorProps) {
        if (prevProps.action !== this.props.action) {
            runInAction(() => {
                this.action = this.props.action;
            });
        }
    }

    unmountHelper = new UnmountHelper(this);

    componentDidMount(): void {
        // only load the console if needed
        const disposeConsoleWatcher = autorun(() => {
            if (this.showConsole) {
                void this.consoleStore.ensureLoaded();
            }
        });

        this.unmountHelper.onUnmount(disposeConsoleWatcher);
    }

    render() {
        // Render from the mirror too, so the whole component sees one action per frame.
        const action = this.action;
        const { title } = this.props;
        return (
            <div className={`actionMonitor ${action.status} ${this.showConsole ? 'showingConsole' : ''}`}>
                <div className="sideBySide">
                    <div className="progressSide">
                        <div className="spinnerSection">
                            {action.status === 'in-progress' && (
                                <>
                                    <span className="serverLoader"></span>&nbsp;
                                    <span className="serverLoader"></span>&nbsp;
                                    <span className="serverLoader"></span>&nbsp;
                                </>
                            )}
                            {action.status === 'completed' && (
                                <span className="text-success">
                                    <i className="icon ion-md-checkmark-circle" />
                                </span>
                            )}
                            {action.status === 'errored' && (
                                <span className="text-danger">
                                    <i className="icon ion-md-alert" />
                                </span>
                            )}
                        </div>

                        <div className="actionProgressSection">
                            <div className="actionProgressDetail">
                                {get(action.progress, 'current_step_detail') ?? title}
                            </div>
                            <ActionStepProgress action={action} />
                        </div>
                        {hasValue(action.blocking_invoice_id) && (
                            <Alert color="warning">
                                This action is blocked pending payment of an{' '}
                                <a href={BillingInvoicesRoute} className="alert-link">
                                    invoice
                                </a>
                            </Alert>
                        )}
                        <div className="actionStepWrapper">
                            <span>Technical Details</span>
                            <div className="actionSteps">
                                <ActionSteps action={action} />
                            </div>
                        </div>
                    </div>
                    {this.showConsole && this.consoleStore.item && (
                        <div className="consoleSection">
                            <ConsolePane
                                console={asViewOnly(this.consoleStore.item)}
                                refreshConsole={() => void this.consoleStore.load()}
                            />
                        </div>
                    )}
                </div>
                {action.status === 'errored' && (
                    <Alert color="danger" role="alert">
                        <ActionErrorMessage errorMessage={action.error_message} linkClassName="alert-link" />
                    </Alert>
                )}
            </div>
        );
    }
}

function asViewOnly(console: Console): Console {
    return {
        ...console,
        iframe: makeViewOnly(console.iframe),
        browser: makeViewOnly(console.browser),
    };
}

function makeViewOnly(consoleUrl: string) {
    if (consoleUrl.includes('view_only=1')) {
        return consoleUrl;
    }
    return `${consoleUrl}${consoleUrl.includes('?') ? '&' : '?'}view_only=1`;
}
